import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DataTable, { type TableColumn } from "react-data-table-component";
import { RefreshCw } from "lucide-react";

import { dataTableCustomStyles } from "../../config/dataTableTheme";
import type { AuthorizationAuditLog } from "../../types/authorizationAdmin";
import { formatDisplayDateTime } from "../../utils/displayDate";
import { Button } from "../atoms/Button";
import { Input } from "../atoms/Input";
import { withScrollableDataTable } from "./withScrollableDataTable";

const ScrollableDataTable = withScrollableDataTable(DataTable);

const PAGE_SIZE = 10;
/** Room for thead + pagination row (RDT `fixedHeaderScrollHeight` = 100% − this). */
const AUDIT_TABLE_CHROME_OFFSET_PX = 132;

type AuthorizationAuditLogsPanelProps = {
  readonly loadLogs: (query: {
    entity_type?: string;
    actor?: string;
    action?: string;
    limit: number;
    offset?: number;
  }) => Promise<{
    data: AuthorizationAuditLog[];
    meta: { total: number; limit: number; offset: number };
  }>;
};

const ENTITY_TYPES = [
  "",
  "ROLE",
  "EXTERNAL_GROUP",
  "ROLE_GROUP_MAPPING",
  "BUSINESS_UNIT",
  "BU_GROUP_MAPPING"
];

export const AuthorizationAuditLogsPanel = ({ loadLogs }: AuthorizationAuditLogsPanelProps) => {
  const [rows, setRows] = useState<AuthorizationAuditLog[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [paginationResetToggle, setPaginationResetToggle] = useState(false);
  const [entityType, setEntityType] = useState("");
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filtersRef = useRef({ entityType, actor, action });
  filtersRef.current = { entityType, actor, action };

  const fetchPage = useCallback(
    async (pageNum: number) => {
      setLoading(true);
      setError(null);
      const { entityType: et, actor: ac, action: act } = filtersRef.current;
      const offset = (pageNum - 1) * PAGE_SIZE;
      try {
        const result = await loadLogs({
          entity_type: et || undefined,
          actor: ac.trim() || undefined,
          action: act.trim() || undefined,
          limit: PAGE_SIZE,
          offset
        });
        setRows(result.data);
        setTotalRows(result.meta.total);
        setPage(pageNum);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unable to load audit log.");
      } finally {
        setLoading(false);
      }
    },
    [loadLogs]
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await loadLogs({ limit: PAGE_SIZE, offset: 0 });
        if (!cancelled) {
          setRows(result.data);
          setTotalRows(result.meta.total);
          setPage(1);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Unable to load audit log.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadLogs]);

  const applyFilters = useCallback(async () => {
    await fetchPage(1);
    setPaginationResetToggle((t) => !t);
  }, [fetchPage]);

  const columns: TableColumn<AuthorizationAuditLog>[] = useMemo(
    () => [
      {
        name: "When",
        selector: (row) => row.created_at,
        width: "200px",
        format: (row) => formatDisplayDateTime(row.created_at)
      },
      { name: "Actor", selector: (row) => row.actor, width: "180px" },
      { name: "Action", selector: (row) => row.action, width: "100px" },
      { name: "Entity", selector: (row) => row.entity_type, width: "160px", grow: 1 }
    ],
    []
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <label htmlFor="audit-entity" className="form-label">
            Entity type
          </label>
          <select
            id="audit-entity"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            className="min-w-[200px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
          >
            {ENTITY_TYPES.map((t) => (
              <option key={t || "all"} value={t}>
                {t || "All"}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <label htmlFor="audit-actor" className="form-label">
            Actor contains
          </label>
          <Input
            id="audit-actor"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            placeholder="email substring"
            className="min-w-[200px]"
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="audit-action" className="form-label">
            Action
          </label>
          <Input
            id="audit-action"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="CREATE, UPDATE…"
            className="w-32"
          />
        </div>
        <Button onClick={() => void applyFilters()} disabled={loading} type="button">
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Apply / Refresh
        </Button>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {/* <div className="box-border flex h-[min(680px,72vh)] min-h-[440px] w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white pb-2"> */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <ScrollableDataTable
            height="500px"
            chromeOffset={AUDIT_TABLE_CHROME_OFFSET_PX}
            columns={columns}
            data={rows}
            customStyles={dataTableCustomStyles}
            keyField="id"
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationPerPage={PAGE_SIZE}
            paginationDefaultPage={page}
            paginationRowsPerPageOptions={[PAGE_SIZE]}
            paginationResetDefaultPage={paginationResetToggle}
            onChangePage={(p) => void fetchPage(p)}
            progressPending={loading}
            noDataComponent={
              <span className="py-6 text-sm text-gray-500">
                No audit entries for this page. Adjust filters and click Apply / Refresh.
              </span>
            }
          />
        </div>
      {/* </div> */}
    </div>
  );
};
